import { useSelector } from 'react-redux';
import { useWatch, useFormState, useFormContext } from 'react-hook-form';
import { selectUserProjectCustomEventManageByApiKey } from 'core/userContext';
import { FormField } from 'ui/formField';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, ModalFooter, ModalHeader } from 'ui/modal';
import { useModalContext } from 'utils/contexts/modal';
import { getApiKeys } from '../selectors';
import { isClientSide } from '../utils';
import { StyledModalBody, BorderedGreySection, StyledModal } from './ApiKeyModal.styled';
import { ModalState, KEY_TYPE, KeyType } from './ApiKeyModal.constants';
import { ApiKey } from '../types';
import { KeyTypeField } from './KeyTypeField';
import { NameField } from './NameField';
import { JwtRequiredField } from './JwtRequiredField';
import { CustomEventAllowCreationField } from './CustomEventAllowCreationField';

type ApiKeyModalProps = {
  id: string;
  buttonText: string;
  headerText: string;
  onSubmit: (values: Record<string, any>) => void;
  disableRequireJwt?: boolean;
  apiKey?: ApiKey;
};

export const ApiKeyModal = ({
  id,
  headerText,
  buttonText,
  onSubmit,
  disableRequireJwt,
  apiKey,
}: ApiKeyModalProps) => {
  const { handleSubmit } = useFormContext();
  const { isValid } = useFormState();
  const keyType = useWatch({
    name: KEY_TYPE,
  });

  const customEventManageByApiKey = useSelector(selectUserProjectCustomEventManageByApiKey);

  const { hideModal } = useModalContext<ModalState>();
  const { keyTypes = [] } = useSelector(getApiKeys);

  const keyCategory = keyTypes?.find(({ value }: KeyType) => value === keyType)?.type;

  return (
    <StyledModal
      id={id}
      isModalOpen={true}
      onClose={hideModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader onClose={hideModal}>{headerText}</ModalHeader>
      <StyledModalBody
        shouldOverflow={isClientSide(keyCategory as string) && Boolean(customEventManageByApiKey)}
      >
        <NameField />
        <KeyTypeField />
        {isClientSide(keyCategory as string) && (
          <FormField labelText="Additional client-side API key settings">
            <BorderedGreySection>
              {customEventManageByApiKey && <CustomEventAllowCreationField apiKey={apiKey} />}
              <JwtRequiredField disableRequireJwt={disableRequireJwt} />
            </BorderedGreySection>
          </FormField>
        )}
      </StyledModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton
          onClick={handleSubmit(onSubmit)}
          isDisabled={!isValid}
          size={BUTTON_SIZES.LARGE}
        >
          {buttonText}
        </PrimaryButton>
      </ModalFooter>
    </StyledModal>
  );
};
